import moment from "moment";
import React, { memo } from "react";
import {
  phoneMask
} from "../../services/Functions";
import { cepMask, cnpjMask, cpfMask } from "../../services/Masks";
import {
  LayoutImpressaoPedido,
  defaultLayoutPedido,
} from "../../types/LayoutImpressao";
import ObservacaoPedido from "../../types/ObservacaoPedido";
import Pagamento from "../../types/Pagamento";
import PedidoUnico from "../../types/PedidoUnico";
import TableProdutos from "../TableProdutos/TableProdutos";
import { OrdenacaoConfig } from "../TableProdutos/OrdenacaoButton/ordenacaoUtils";
import { ContainerPrintablePedido } from "./styles";

interface PrintablePedidoProps {
  pedido: PedidoUnico | undefined;
  informacoesPagamento: Pagamento | undefined;
  layoutConfig?: LayoutImpressaoPedido;
  ordenacao?: OrdenacaoConfig;
  observacoes?: ObservacaoPedido[];
}

const PrintablePedido: React.ForwardRefRenderFunction<
  HTMLDivElement,
  PrintablePedidoProps
> = ({ pedido, informacoesPagamento, layoutConfig = defaultLayoutPedido, ordenacao, observacoes = [] }, ref) => {
  const config = layoutConfig;

  const meiosDePagamento = {
    pix: "Pix",
    cartao: "Cartão",
    a_combinar: "A combinar",
  };

  const intermediadores = {
    pagseguro: "PagSeguro",
    pagarme: "Pagarme",
  };

  const obterInformacoesContato = (cliente) => {
    return [
      config.telefone && cliente.telefone && phoneMask(cliente.telefone),
      config.email && cliente.email,
      config.dataNascimento && cliente.data_nascimento &&
        `data de nascimento: ${moment(cliente.data_nascimento).format("DD/MM/YYYY")}`,
    ].filter(Boolean).join(", ");
  };

  const obterInformacoesFiscais = (cliente) => {
    return [
      config.cpf && cliente.cpf && `CPF: ${cpfMask(cliente.cpf)}`,
      config.cnpj && cliente.cnpj && `CNPJ: ${cnpjMask(cliente.cnpj)}`,
      config.inscricaoEstadual && cliente.inscricao_estadual && `IE: ${cpfMask(cliente.inscricao_estadual)}`,
    ].filter(Boolean).join(", ");
  };

  const obterEnderecoCliente = (cliente) => {
    if (!config.endereco) return "";
    return [
      `${cliente.endereco || ""} ${cliente.numero || ""}`.trim(),
      cliente.complemento,
      cliente.bairro,
      cliente.cidade && cliente.estado && `${cliente.cidade} / ${cliente.estado}`,
      cliente.cep && cepMask(cliente.cep),
    ].filter(Boolean).join(", ");
  };

  const montarInformacoesCliente = () => {
    if (pedido && pedido.cliente) {
      const { cliente } = pedido;
      const contato = obterInformacoesContato(cliente);
      const fiscais = obterInformacoesFiscais(cliente);
      const endereco = obterEnderecoCliente(cliente);

      return (
        <>
          {contato && <span>{contato}<br /></span>}
          {fiscais && <span>{fiscais}<br /></span>}
          {endereco && <span>{endereco}<br /></span>}
        </>
      );
    }
    return null;
  };

  const montarInformacoesPedido = () => {
    if (pedido && pedido.valor_total && informacoesPagamento) {
      const informacoes = [
        pedido.valor_total &&
        `Valor dos produtos: ${pedido.moeda} ${Number.parseFloat(pedido?.valor_total || "0")
            .toFixed(2)
            .replace(".", ",")}`,
        config.descontoProgressivo && pedido.desconto_progressivo?.valor && pedido.desconto_progressivo.valor > 0 &&
          ` - ${pedido?.moeda} ${pedido.desconto_progressivo.valor
            .toFixed(2)
            .replace(".", ",")} de desconto progressivo (-${pedido.desconto_progressivo.percentual}%)`,
        config.descontoCupom && Number.parseFloat(pedido.desconto_cupom?.toString() || "0") > 0 &&
            ` - ${pedido?.moeda} ${(calcularDescontoCupom() / 100)
              .toFixed(2)
              .replace(".", ",")} de desconto do cupom (${pedido?.tipo_desconto === "porcentagem" ? "%" : pedido?.moeda || "R$"}) `,
        config.descontoPix && Number.parseFloat(informacoesPagamento.desconto || "0") > 0 &&
          ` - ${pedido?.moeda} ${calcularDescontoPix()
            .toFixed(2)
            .replace(".", ",")} de desconto do pix`,
        config.frete && pedido.frete?.valor_frete &&
          Number.parseFloat(pedido.frete?.valor_frete) > 0 &&
          ` + Valor do frete: ${pedido?.moeda} ${Number.parseFloat(
            pedido.frete.valor_frete
          ).toFixed(2).replace(".", ",")}`,
      ];
      return informacoes.filter(Boolean).join("");
    }
  };

  const switchFormasDePagamento = () => {
    if (!informacoesPagamento || !config.pagamento) {
      return null;
    }

    const { tipo, intermediador, cartao, parcelas, id_transacao, pix_code } = informacoesPagamento;

    const informacoes = [
      intermediadores[intermediador],
      meiosDePagamento[tipo] && meiosDePagamento[tipo],
      cartao &&
        `${cartao[0].toUpperCase() + cartao.slice(1).toLowerCase()} x${
          parcelas || ""
        }`,
      id_transacao && id_transacao !== "0" ? id_transacao : "",
      pix_code &&
        `Chave pix: ${
          pix_code.length > 20 ? pix_code.slice(0, 20) + "..." : pix_code
        }`,
    ];

    return informacoes.filter(Boolean).join(", ");
  };
  
  const formatarTipoFrete = (frete: string | undefined) => {
    if (!frete) return "Não informado";
  
    const fretesLabels = {
      entrega_local: "Entrega local",
      frete_gratis: "Frete grátis",
      retirada_no_local: "Retirada no local",
      a_combinar: "A combinar",
    };
  
    frete.startsWith("especial_")
      ? (frete = `Especial - ${frete.replace("especial_", "")}`)
      : (frete = fretesLabels[frete] || frete.toUpperCase());
  
    return frete;
  };

  const calcularDescontoProgressivo = () => {
    return pedido?.desconto_progressivo?.valor || 0;
  };

  const calcularDescontoCupom = () => {
    if (!pedido?.desconto_cupom || !pedido?.valor_total) return 0;

    const desconto = Number(pedido.desconto_cupom);
    const valorTotal = Number.parseFloat(pedido.valor_total);
    const valorAposProgressivo = valorTotal - calcularDescontoProgressivo();

    if (pedido.tipo_desconto === "porcentagem") {
      return (desconto / 100) * valorAposProgressivo;
    } else {
      return desconto;
    }
  };

  const calcularDescontoPix = () => {
    if (!informacoesPagamento?.desconto) return 0;

    const percentualPix = Number.parseFloat(informacoesPagamento.desconto) / 100;
    const valorTotal = Number.parseFloat(pedido?.valor_total || "0");
    const descontoCupomReais = calcularDescontoCupom() / 100;
    const valorAposCupom = valorTotal - calcularDescontoProgressivo() - descontoCupomReais;

    return percentualPix * valorAposCupom;
  };
  
  const temInfoHeader = config.numeroPedido || config.dataPedido || config.catalogo || config.vendedor;
  const temInfoCliente = montarInformacoesCliente();
  const temInfoValor = config.valorTotal;
  const temInfoPagamento = switchFormasDePagamento();
  const temInfoFrete = config.frete;

  return (
    <>
      {pedido && informacoesPagamento && (
        <ContainerPrintablePedido ref={ref}>
          {temInfoHeader && (
            <>
              <div className="container">
                <div className="flex">
                  {config.numeroPedido && (
                    <div className="titulo fonteTitulo">Pedido #{pedido.id_pedido_sequencial}</div>
                  )}
                  {config.dataPedido && (
                    <div className="fonteTexto">
                      Data: {moment(pedido.data).format("DD/MM/YYYY HH:mm:ss")}
                    </div>
                  )}
                </div>
                {(config.catalogo || config.vendedor) && (
                  <div className="flex fonteSubtitulo">
                    {[
                      config.catalogo && `Catálogo: ${pedido.catalogo!.descricao}`,
                      config.vendedor && `Vendedor: ${pedido.nome}`,
                    ].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
              <div className="container-linha">
                <div className="linha"></div>
              </div>
            </>
          )}
          {temInfoCliente && (
            <>
              <div className="container">
                <div className="flex fonteSubtitulo">
                  <div className="fonteSubtitulo">
                    <span className="boldInicial fonteSubtitulo">
                      {pedido.cliente!.nome
                        ? pedido.cliente!.nome
                        : pedido.cliente!.razao_social}{" "}
                      -{" "}
                    </span>
                    {temInfoCliente}
                  </div>
                </div>
              </div>
              <div className="container-linha">
                <div className="linha"></div>
              </div>
            </>
          )}
          {(temInfoValor || temInfoPagamento || temInfoFrete) && (
            <>
              <div className="container">
                <div className="flex">
                  <div className="single-line">
                    {temInfoValor && (
                      <>
                        <span className="boldInicial fonteSubtitulo">Valor total do pedido</span>{" "}
                        <span>
                          {pedido.moeda} {(Number.parseFloat(informacoesPagamento?.valor_cobranca) / 100)
                            .toFixed(2)
                            .replace(".", ",")}
                        </span>
                        <br />
                        <span>{montarInformacoesPedido()}</span>
                        <br />
                      </>
                    )}
                    {temInfoPagamento && (
                      <>
                        <span>{temInfoPagamento}</span>
                        {temInfoFrete && ", "}
                      </>
                    )}
                    {temInfoFrete && (
                      <span>
                        {formatarTipoFrete(pedido.frete?.tipo_frete)}
                        {pedido.frete?.prazo_frete && Number(pedido.frete?.prazo_frete) > 0
                          ? ` - ${pedido.frete?.prazo_frete} dia(s)`
                          : ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="container-linha">
                <div className="linha"></div>
              </div>
            </>
          )}
          <br />
          <div className="container fonteSubtitulo">
            <TableProdutos
              produtos={pedido.produtos}
              moeda={pedido.moeda}
              template="impressao"
              layoutConfig={config.tabelaProdutos}
              ordenacaoExterna={ordenacao}
            />
          </div>
          {config.observacaoInterna && observacoes.filter(obs => !obs.visivel_cliente).length > 0 && (
            <>
              <div className="container-linha" />
              <div className="container">
                <div className="flex">
                  <div>
                    <span>Observação interna: </span> <br />
                    {observacoes
                      .filter(obs => !obs.visivel_cliente)
                      .map(obs => obs.texto)
                      .join(" | ")}
                  </div>
                </div>
              </div>
            </>
          )}
          {config.observacaoCliente && pedido.cliente.observacao && (
            <>
              <div className="container-linha" />
              <div className="container">
                <div className="flex">
                  <div>
                    <span>Observação do pedido: </span> <br />
                    {pedido.cliente.observacao}
                  </div>
                </div>
              </div>
            </>
          )}
        </ContainerPrintablePedido>
      )}
    </>
  );  
};

export default memo(React.forwardRef(PrintablePedido));
